<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { type CopilotConfigurationTemplate, PolicyName } from '@factoryfixinc/ats-interfaces';

import CopilotConfigurationTemplateService from '@/core/copilot-configuration-template/copilot-configuration-template.service';
import { ErrorService } from '@/core/shared/errors/error.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import MeService from '@/core/shared/me/me.service';
import { EmployerService } from '@/core/shared/employer/employer.service';

import PlusIcon from '@/assets/svg/plus.svg?component';

import CopilotPersonalityCardList from '@/components/CopilotPersonalities/CopilotPersonalityCardList/CopilotPersonalityCardList.vue';
import CopilotPersonalityCreationModal from '@/components/CopilotPersonalities/CopilotPersonalityCreationModal/CopilotPersonalityCreationModal.vue';
import CopilotPersonalityConfirmDeleteModal from '@/components/CopilotPersonalities/CopilotPersonalityConfirmDeleteModal/CopilotPersonalityConfirmDeleteModal.vue';
import CopilotPersonalityTransferOwnershipModal from '@/components/CopilotPersonalities/CopilotPersonalityTransferOwnershipModal/CopilotPersonalityTransferOwnershipModal.vue';
import type { CopilotConfigurationFormData } from '@/components/CopilotPersonalities/types/copilot-configuration-template.interface';

// Services
// ===================
const copilotConfigurationTemplateService = new CopilotConfigurationTemplateService();
const employerService = new EmployerService();
const meService = new MeService();

// Reactive state
// ===================
const isCreationModalOpen = ref(false);
const isSavingRecruiter = ref(false);
const isDeleteModalOpen = ref(false);
const isLoading = ref(false);
const isDeletingTemplate = ref(false);
const editingTemplateId = ref<number | undefined>(undefined);
const deletingTemplateCopilotName = ref<string>('');
const deletingTemplateJobCount = ref<number>(0);
const deletingTemplateId = ref<number | undefined>(undefined);
const isTransferModalOpen = ref(false);
const isTransferringOwnership = ref(false);
const transferringTemplate = ref<
  (CopilotConfigurationTemplate & { projectCount?: number }) | undefined
>(undefined);

// Computed Properties
// ===================
const myUserProfileId = computed(() => {
  return meService.userProfile?.id ?? 0;
});

const aiRecruiters = computed(() => {
  return copilotConfigurationTemplateService.templates;
});

const defaultCopilotJobCount = computed(
  () => copilotConfigurationTemplateService.defaultCopilotJobCount,
);

const candidateAiDisclosurePolicy = computed(() =>
  employerService.getPolicyByName(PolicyName.AI_DISCLOSURE),
);

// Methods
// ===================
const fetchAIRecruiters = async () => {
  try {
    isLoading.value = true;
    const [templatesResult, policiesResult, defaultCountResult] = await Promise.allSettled([
      copilotConfigurationTemplateService.fetchTemplates(),
      employerService.fetchEmployerPolicies(),
      copilotConfigurationTemplateService.fetchDefaultCopilotJobCount(),
    ]);

    if (templatesResult.status === 'rejected') {
      throw templatesResult.reason;
    }

    if (policiesResult.status === 'rejected') {
      ErrorService.captureException(policiesResult.reason);
    }

    // The default card just hides its job count if this fails; the page still works
    if (defaultCountResult.status === 'rejected') {
      ErrorService.captureException(defaultCountResult.reason);
    }
  } catch (err) {
    ErrorService.captureException(err);
  } finally {
    isLoading.value = false;
  }
};

const handleSaveRecruiter = async (eventData: {
  data: CopilotConfigurationFormData;
  templateId?: number;
}) => {
  if (isSavingRecruiter.value) return;

  const { copilotName, copilotDescription, ...snapshot } = eventData.data;

  if (!copilotName?.trim()) {
    SnackbarService.critical('Recruiter name is required');
    return;
  }

  const templateId = eventData.templateId;
  const parsedTemplateId = Number(templateId);
  const isUpdatingRecruiter = !isNaN(parsedTemplateId) && Number.isInteger(parsedTemplateId);
  const successMessage = isUpdatingRecruiter
    ? 'Recruiter updated successfully'
    : 'Recruiter created successfully';

  isSavingRecruiter.value = true;

  try {
    if (isUpdatingRecruiter) {
      await copilotConfigurationTemplateService.updateTemplate(
        parsedTemplateId,
        {
          displayName: copilotName,
          description: copilotDescription,
          snapshot: {
            ...snapshot,
            copilotName,
          },
        },
        candidateAiDisclosurePolicy.value,
      );
    } else {
      await copilotConfigurationTemplateService.createTemplate(
        copilotName,
        {
          ...snapshot,
          copilotName,
        },
        copilotDescription,
        candidateAiDisclosurePolicy.value,
      );
    }
  } catch (err) {
    ErrorService.captureException(err);
    SnackbarService.critical('Failed to create recruiter, try again later');
    return;
  } finally {
    isSavingRecruiter.value = false;
  }

  SnackbarService.success(successMessage);
  handleCloseCreationModal();
  await fetchAIRecruiters();
};

const openCreationModal = () => {
  isCreationModalOpen.value = true;
};

const handleCloseCreationModal = () => {
  isCreationModalOpen.value = false;
  editingTemplateId.value = undefined;
};

const handleEditTemplate = (templateId: number) => {
  editingTemplateId.value = templateId;
  isCreationModalOpen.value = true;
};

const handleDeleteTemplate = (templateId: number) => {
  deletingTemplateId.value = templateId;
  const template = aiRecruiters.value.find((template) => template.id === templateId);

  if (template) {
    const foundTemplate: CopilotConfigurationTemplate & { projectCount?: number } = template;
    deletingTemplateCopilotName.value = foundTemplate.displayName;
    deletingTemplateJobCount.value = foundTemplate.projectCount ?? 0;
    isDeleteModalOpen.value = true;
  }
};

const handleCloseDeleteModal = () => {
  isDeleteModalOpen.value = false;
};

const handleModalAfterLeaveEvent = () => {
  deletingTemplateId.value = undefined;
  deletingTemplateCopilotName.value = '';
  deletingTemplateJobCount.value = 0;
};

const deleteCopilotTemplate = async () => {
  try {
    isDeletingTemplate.value = true;
    if (deletingTemplateId.value) {
      await copilotConfigurationTemplateService.deleteTemplate(deletingTemplateId.value);
    }

    SnackbarService.success('Recruiter deleted successfully');
    await fetchAIRecruiters();
  } catch (err) {
    ErrorService.captureException(err);
    SnackbarService.critical('Failed to delete recruiter, try again later');
  } finally {
    isDeleteModalOpen.value = false;
    deletingTemplateId.value = undefined;
    deletingTemplateCopilotName.value = '';
    deletingTemplateJobCount.value = 0;
    isDeletingTemplate.value = false;
  }
};

const handleTransferTemplate = (templateId: number) => {
  const template = aiRecruiters.value.find((template) => template.id === templateId);

  if (template) {
    transferringTemplate.value = template;
    isTransferModalOpen.value = true;
  }
};

const handleCloseTransferModal = () => {
  isTransferModalOpen.value = false;
};

const handleTransferModalAfterLeaveEvent = () => {
  transferringTemplate.value = undefined;
};

const transferCopilotTemplateOwnership = async (ownerId: number | null) => {
  const templateId = transferringTemplate.value?.id;

  if (!templateId) {
    return;
  }

  try {
    isTransferringOwnership.value = true;
    await copilotConfigurationTemplateService.transferTemplateOwnership(templateId, ownerId);

    SnackbarService.success(
      ownerId === null ? 'Recruiter is now team-owned' : 'Ownership transferred successfully',
    );
    isTransferModalOpen.value = false;
    await fetchAIRecruiters();
  } catch (err) {
    ErrorService.captureException(err);
    SnackbarService.critical('Failed to transfer ownership, try again later');
  } finally {
    isTransferringOwnership.value = false;
  }
};

// Lifecycle Hooks
// ===============
onMounted(() => {
  fetchAIRecruiters();
});
</script>

<template>
  <div class="min-h-screen bg-highlight-0">
    <div class="mx-auto max-w-[1420px] px-4 py-5 sm:px-8">
      <!-- Header -->
      <div class="mb-4 flex flex-col items-start gap-4 sm:flex-row sm:justify-between sm:gap-0">
        <!-- Title and subtitle -->
        <div>
          <h1 class="mb-1 font-serif text-lg font-black leading-6 text-black">
            My AI recruiting team
          </h1>
          <p class="text-base font-normal text-shade-820">
            Create a team of AI recruiters that work 24/7 to find the best candidates for your jobs
          </p>
        </div>

        <!-- New AI recruiter button -->
        <button
          data-test="new-ai-recruiter"
          class="flex h-10 w-full cursor-pointer items-center justify-center rounded-md bg-highlight-500 px-4 py-2.5 hover:!bg-highlight-600 sm:w-auto sm:min-w-[174px]"
          @click="openCreationModal"
        >
          <figure class="inline-block h-5 w-5 items-center justify-center px-0.5 py-0.5">
            <PlusIcon class="text-tint-0" />
          </figure>
          <span class="ml-2 font-sans text-sm font-semibold text-tint-0"> New AI recruiter</span>
        </button>
      </div>

      <!-- Copilot Personalities -->
      <CopilotPersonalityCardList
        :copilot-configuration-templates="aiRecruiters"
        :owner-id="myUserProfileId"
        :default-copilot-active-job-count="defaultCopilotJobCount"
        :is-loading="isLoading"
        @edit-template="handleEditTemplate"
        @delete-template="handleDeleteTemplate"
        @transfer-template="handleTransferTemplate"
      />

      <!-- Copilot Personality Modal -->
      <CopilotPersonalityCreationModal
        v-model="isCreationModalOpen"
        :copilot-configuration-template-id="editingTemplateId"
        :candidate-ai-disclosure-policy="candidateAiDisclosurePolicy"
        :is-saving="isSavingRecruiter"
        @save="handleSaveRecruiter"
        @close="handleCloseCreationModal"
      />

      <!-- Copilot Personality Confirm Delete Modal -->
      <CopilotPersonalityConfirmDeleteModal
        :is-open="isDeleteModalOpen"
        :is-loading="isDeletingTemplate"
        :copilot-name="deletingTemplateCopilotName"
        :job-count="deletingTemplateJobCount"
        @delete="deleteCopilotTemplate"
        @cancel="handleCloseDeleteModal"
        @after-leave="handleModalAfterLeaveEvent"
      />

      <!-- Copilot Personality Transfer Ownership Modal -->
      <CopilotPersonalityTransferOwnershipModal
        :is-open="isTransferModalOpen"
        :is-loading="isTransferringOwnership"
        :copilot-name="transferringTemplate?.displayName ?? ''"
        :job-count="transferringTemplate?.projectCount ?? 0"
        :current-owner-id="transferringTemplate?.ownerId ?? null"
        @transfer="transferCopilotTemplateOwnership"
        @cancel="handleCloseTransferModal"
        @after-leave="handleTransferModalAfterLeaveEvent"
      />
    </div>
  </div>
</template>
